import { FC, ReactNode, useState } from "react";
import { dollars } from "./dollars";
import {
  BoltIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  UserPlusIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import ProductDetails from "./ProductDetails";

/** A glyph for an extra, from its name: the operator names them freely, there is no icon field. */
export const iconFor = (name: string) => {
  const n = name.toLowerCase();
  if (/driver/.test(n)) return UserPlusIcon;
  if (/gps|navig|map/.test(n)) return MapPinIcon;
  if (/seat|child|baby|booster|kid/.test(n)) return UserGroupIcon;
  if (/fuel|gas|petrol|charg|\bev\b/.test(n)) return BoltIcon;
  if (/wi-?fi|internet|hotspot/.test(n)) return WifiIcon;
  if (/insur|protect|cover|accident/.test(n)) return ShieldCheckIcon;
  return SparklesIcon;
};

const Switch: FC<{ on: boolean; disabled?: boolean; label: string; onChange: (on: boolean) => void }> = ({
  on,
  disabled,
  label,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
      on ? "bg-text-color" : "bg-text-color/30"
    }`}
  >
    <span
      className={`absolute top-[3px] h-5 w-5 rounded-full bg-bg-color transition-all ${on ? "left-[22px]" : "left-[3px]"}`}
    />
  </button>
);

const ExtraCard: FC<{
  name: string;
  price: string;
  unit: string;
  badge?: string;
  on: boolean;
  locked?: boolean;
  onChange: (on: boolean) => void;
  hasDetails: boolean;
  children?: ReactNode;
  open: boolean;
  onToggle: () => void;
}> = ({ name, price, unit, badge, on, locked, onChange, hasDetails, children, open, onToggle }) => {
  const Icon = iconFor(name);
  return (
    <div className={`rounded-2xl border-2 bg-card-color p-3 ${on ? "border-text-color" : "border-secondary-100"}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100/60">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block break-words text-[15px] font-extrabold leading-tight">{name}</span>
          {badge && (
            <span className="mt-0.5 inline-block rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
              {badge}
            </span>
          )}
        </div>
        <Switch on={on} disabled={locked} label={name} onChange={onChange} />
      </div>
      <div className="ml-12 mt-1">
        <b className="text-base">{price}</b> <span className="text-xs opacity-70">/ {unit}</span>
        {hasDetails && (
          <div>
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className="min-h-[32px] text-xs font-bold underline"
            >
              Details
            </button>
          </div>
        )}
        {open && children}
      </div>
    </div>
  );
};

/**
 * Step 3 of the phone booking: extras as cards with a toggle (1NOW-11155). The
 * toggles write through the page's own handlers, so required extras stay on
 * and a Bonzah add-on still asks for the Bonzah terms first.
 */
const ExtrasStep: FC<{
  extras: any[];
  addons: any[];
  selectedExtras: Set<any>;
  onExtra: (id: any, on: boolean) => void;
  selectedAddons: Set<any>;
  /** attemptAddonSelection: toggles, behind the Bonzah terms the first time. */
  onAddon: (value: string) => void;
  bgColor?: string;
}> = ({ extras, addons, selectedExtras, onExtra, selectedAddons, onAddon, bgColor }) => {
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (k: string) => setOpen((o) => (o === k ? null : k));

  if (extras.length === 0 && addons.length === 0)
    return <p className="rounded-2xl border border-secondary-100 p-4 text-sm">This car has no extras to add.</p>;

  return (
    <div className="flex flex-col gap-3">
      {addons.map((a) => {
        const v = a.value?.[0];
        return (
          <ExtraCard
            key={`addon-${a.title}`}
            open={open === `addon-${a.title}`}
            onToggle={() => toggle(`addon-${a.title}`)}
            name={a.title}
            price={dollars(a.price)}
            unit="day"
            on={selectedAddons.has(v)}
            onChange={() => onAddon(v)}
            hasDetails
          >
            <ProductDetails product={a} bgColor={bgColor} bonzahLinks />
          </ExtraCard>
        );
      })}
      {extras.map((x) => (
        <ExtraCard
          key={`extra-${x.id}`}
          open={open === `extra-${x.id}`}
          onToggle={() => toggle(`extra-${x.id}`)}
          name={x.name}
          price={dollars(x.amount)}
          unit={x.nature === "per_day" ? "day" : "one-time"}
          badge={x.is_required ? "Required" : x.is_recommended === true ? "Recommended" : undefined}
          on={selectedExtras.has(x.id)}
          locked={x.is_required}
          onChange={(on) => onExtra(x.id, on)}
          hasDetails={Boolean(x.description)}
        >
          <p className="mt-1 whitespace-pre-line break-words text-sm">{x.description}</p>
        </ExtraCard>
      ))}
    </div>
  );
};

export default ExtrasStep;
